// Wave 3 — ADR-0002 §call_path rendering. The web GUI is the canonical "side-by-side
// rich graph" venue: foldable tree (left) + cytoscape force-directed graph (right).
// Edges colored by edge_kind so the GraphQL bridge hops are visually distinct from
// direct / celery_delay / etc.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import cytoscape from 'cytoscape';
import dagre from 'cytoscape-dagre';
import { ChevronDown, ChevronRight, GitBranch, Network, Search } from 'lucide-react';

import { callPath, getFile } from '../lib/api';
import type { CallPath, CallPathEdge, CallPathResponse, FilePreview } from '../lib/types';
import type { Theme } from '../lib/shiki';
import { CodeBlock } from '../components/CodeBlock';

// Register the dagre extension once. Idempotent — cytoscape guards against double-register.
cytoscape.use(dagre);

interface Props {
  theme: Theme;
}

// Edge-kind palette. graphql_bridge is orange so the cross-stack hop pops; direct grey;
// dynamic-dispatch heuristics get warmer colors so an agent / human can see them at a glance.
const EDGE_COLORS: Record<string, string> = {
  direct: '#9CA3AF',                   // zinc-400
  graphql_bridge: '#FB923C',           // orange-400
  celery_delay: '#A78BFA',             // violet-400
  signal_send: '#F472B6',              // pink-400
  decorator_wrapped: '#34D399',        // emerald-400
  dispatch_method: '#60A5FA',          // blue-400
  attribute_call_in_class: '#FBBF24',  // amber-400
};
const EDGE_COLOR_DEFAULT = '#9CA3AF';

function colorFor(kind: string): string {
  return EDGE_COLORS[kind] ?? EDGE_COLOR_DEFAULT;
}

export function CallPathPage({ theme }: Props) {
  const [fromQn, setFromQn] = useState('');
  const [toQn, setToQn] = useState('');
  const [maxDepth, setMaxDepth] = useState(6);
  const [viaGraphql, setViaGraphql] = useState(true);
  const [response, setResponse] = useState<CallPathResponse | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<CallPathEdge | null>(null);

  const mutation = useMutation({
    mutationFn: (): Promise<CallPathResponse> =>
      callPath({ from: fromQn.trim(), to: toQn.trim(), maxDepth, viaGraphql }),
    onSuccess: (data) => {
      setResponse(data);
      setSelectedEdge(null);
    },
    onError: (err) => {
      // Keep the previous response visible so the user can retry without losing context.
      // eslint-disable-next-line no-console
      console.error('call_path failed', err);
    },
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!fromQn.trim() || !toQn.trim()) return;
    mutation.mutate();
  }

  return (
    <main className="flex-1 min-w-0 flex flex-col bg-emerald-50/30 dark:bg-emerald-950/10">
      <div className="border-b border-zinc-200 dark:border-zinc-800 px-4 py-3 bg-white dark:bg-zinc-900">
        <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <GitBranch size={18} /> Call Path
        </h2>
        <p className="text-xs text-zinc-500 mb-3">
          Up to 5 shortest static paths between two qualified names. <strong>via_graphql</strong>{' '}
          synthesizes a cross-stack hop at every FE GraphQL hook callsite (orange edges).
        </p>
        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col">
            <label className="text-xs uppercase tracking-wide text-zinc-500">from</label>
            <input
              type="text"
              value={fromQn}
              onChange={(e) => setFromQn(e.target.value)}
              placeholder="e.g. fakeapp.useGetMeQuery"
              className="w-96 px-2 py-1 text-sm rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 font-mono"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs uppercase tracking-wide text-zinc-500">to</label>
            <input
              type="text"
              value={toQn}
              onChange={(e) => setToQn(e.target.value)}
              placeholder="e.g. enrollwise.api.graphql.utils.get_resolved_id_from_global_id"
              className="w-96 px-2 py-1 text-sm rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 font-mono"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs uppercase tracking-wide text-zinc-500">max depth</label>
            <input
              type="number"
              min={1}
              max={12}
              value={maxDepth}
              onChange={(e) => setMaxDepth(Math.max(1, Math.min(12, Number(e.target.value) || 6)))}
              className="w-20 px-2 py-1 text-sm rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900"
            />
          </div>
          <label className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={viaGraphql}
              onChange={(e) => setViaGraphql(e.target.checked)}
            />
            via_graphql
          </label>
          <button
            type="submit"
            disabled={mutation.isPending || !fromQn.trim() || !toQn.trim()}
            className="px-3 py-1 rounded bg-emerald-600 text-white text-sm flex items-center gap-1 disabled:opacity-50"
          >
            <Search size={14} /> {mutation.isPending ? 'Finding…' : 'Find paths'}
          </button>
        </form>
        {response?.advisory && (
          <p className="mt-2 text-xs italic text-amber-700 dark:text-amber-400">
            {response.advisory}
          </p>
        )}
      </div>

      <div className="flex-1 min-h-0 flex">
        {/* Left pane — foldable tree of paths */}
        <section className="w-1/2 min-w-0 border-r border-zinc-200 dark:border-zinc-800 overflow-y-auto p-3">
          {response === null && (
            <div className="text-sm text-zinc-500">No query yet. Enter from/to and submit.</div>
          )}
          {response && response.paths.length === 0 && (
            <div className="text-sm text-zinc-500">No path found within {maxDepth} hops.</div>
          )}
          {response && response.paths.length > 0 && (
            <PathsTree paths={response.paths} onSelectEdge={setSelectedEdge} />
          )}
        </section>

        {/* Right pane — cytoscape force-directed graph + selected-edge file preview */}
        <section className="w-1/2 min-w-0 flex flex-col">
          <div className="flex-1 min-h-0 border-b border-zinc-200 dark:border-zinc-800 relative">
            <div className="absolute top-2 left-2 text-xs flex items-center gap-1 px-2 py-1 rounded bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 z-10">
              <Network size={12} /> Graph
            </div>
            <PathsGraph paths={response?.paths ?? []} onSelectEdge={setSelectedEdge} theme={theme} />
            <Legend />
          </div>
          <div className="h-2/5 overflow-y-auto p-3 bg-white dark:bg-zinc-900">
            {selectedEdge ? (
              <EdgePreview edge={selectedEdge} theme={theme} />
            ) : (
              <div className="text-xs text-zinc-500">
                Click an edge in the tree or a node in the graph to preview source.
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

// ---- foldable tree -----------------------------------------------------------

function PathsTree({
  paths,
  onSelectEdge,
}: {
  paths: CallPath[];
  onSelectEdge: (edge: CallPathEdge) => void;
}) {
  return (
    <ul className="space-y-2">
      {paths.map((p, i) => (
        <PathNode key={i} path={p} index={i} onSelectEdge={onSelectEdge} />
      ))}
    </ul>
  );
}

function PathNode({
  path,
  index,
  onSelectEdge,
}: {
  path: CallPath;
  index: number;
  onSelectEdge: (edge: CallPathEdge) => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <li className="border border-zinc-200 dark:border-zinc-800 rounded bg-white dark:bg-zinc-900">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-1 text-left px-2 py-1 text-sm hover:bg-zinc-50 dark:hover:bg-zinc-800"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Path {index + 1} <span className="text-xs text-zinc-500">(length {path.length})</span>
      </button>
      {open && (
        <ol className="pl-6 pb-2 space-y-1 font-mono text-xs">
          {path.edges.map((e, j) => (
            <li key={j}>
              <button
                onClick={() => onSelectEdge(e)}
                className="text-left hover:underline"
                title={`${e.file_path}:${e.line}`}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full mr-1 align-middle"
                  style={{ backgroundColor: colorFor(e.edge_kind) }}
                />
                <span className="text-zinc-500">[{e.edge_kind}]</span>{' '}
                {e.caller_qual_name} → {e.resolved_callee_qual_name ?? e.callee_name}{' '}
                <span className="text-zinc-500">
                  ({e.file_path}:{e.line})
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </li>
  );
}

// ---- cytoscape graph ---------------------------------------------------------

function PathsGraph({
  paths,
  onSelectEdge,
  theme,
}: {
  paths: CallPath[];
  onSelectEdge: (edge: CallPathEdge) => void;
  theme: Theme;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);

  // Build the elements array from all paths combined: each unique qual_name is a node,
  // each edge is a directed edge with edge_kind metadata.
  const elements = useMemo(() => {
    const nodes = new Map<string, cytoscape.ElementDefinition>();
    const edges: cytoscape.ElementDefinition[] = [];
    for (const p of paths) {
      for (const e of p.edges) {
        const target = e.resolved_callee_qual_name ?? e.callee_name;
        if (!nodes.has(e.caller_qual_name)) {
          nodes.set(e.caller_qual_name, {
            data: { id: e.caller_qual_name, label: shortenQn(e.caller_qual_name) },
          });
        }
        if (!nodes.has(target)) {
          nodes.set(target, { data: { id: target, label: shortenQn(target) } });
        }
        edges.push({
          data: {
            id: `${e.caller_qual_name}->${target}@${e.file_path}:${e.line}`,
            source: e.caller_qual_name,
            target,
            kind: e.edge_kind,
            color: colorFor(e.edge_kind),
            edge: e,
          },
        });
      }
    }
    return [...nodes.values(), ...edges];
  }, [paths]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (cyRef.current) {
      cyRef.current.destroy();
      cyRef.current = null;
    }
    const isDark = theme === 'dark';
    const cy = cytoscape({
      container: containerRef.current,
      elements,
      style: [
        {
          selector: 'node',
          style: {
            label: 'data(label)',
            'font-size': '10px',
            'background-color': isDark ? '#1f2937' : '#e5e7eb',
            color: isDark ? '#f4f4f5' : '#18181b',
            'border-width': 1,
            'border-color': isDark ? '#3f3f46' : '#a1a1aa',
            'text-valign': 'center',
            'text-halign': 'center',
            width: 'label',
            height: 'label',
            padding: '8px',
            shape: 'round-rectangle',
          },
        },
        {
          selector: 'edge',
          style: {
            width: 2,
            'line-color': 'data(color)',
            'target-arrow-color': 'data(color)',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            label: 'data(kind)',
            'font-size': '8px',
            color: isDark ? '#a1a1aa' : '#52525b',
            'text-rotation': 'autorotate',
            'text-margin-y': -8,
          },
        },
      ],
      layout: { name: 'dagre' as 'preset', rankDir: 'LR', nodeSep: 50, rankSep: 80 } as any,
      wheelSensitivity: 0.2,
    });
    cy.on('tap', 'edge', (evt) => {
      const e = evt.target.data('edge') as CallPathEdge | undefined;
      if (e) onSelectEdge(e);
    });
    cy.on('tap', 'node', (evt) => {
      // Surface the first incident edge so the file preview has something to show.
      const node = evt.target;
      const incident = node.connectedEdges().first();
      if (incident && incident.length > 0) {
        const e = incident.data('edge') as CallPathEdge | undefined;
        if (e) onSelectEdge(e);
      }
    });
    cyRef.current = cy;
    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [elements, theme, onSelectEdge]);

  return <div ref={containerRef} className="w-full h-full" />;
}

function shortenQn(qn: string): string {
  // Trailing two segments are usually enough to identify a node uniquely in-graph; the
  // tooltip on hover would surface the full qual_name (cytoscape title via a dedicated
  // overlay — wave 4 if useful).
  const parts = qn.split('.');
  if (parts.length <= 2) return qn;
  return `…${parts.slice(-2).join('.')}`;
}

function Legend() {
  const items = Object.entries(EDGE_COLORS);
  return (
    <div className="absolute bottom-2 right-2 text-xs px-2 py-1 rounded bg-white/90 dark:bg-zinc-900/90 border border-zinc-200 dark:border-zinc-800 z-10">
      <div className="font-semibold mb-1">edge kinds</div>
      {items.map(([k, c]) => (
        <div key={k} className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: c }} />
          {k}
        </div>
      ))}
    </div>
  );
}

// ---- selected-edge file preview ---------------------------------------------

function EdgePreview({ edge, theme }: { edge: CallPathEdge; theme: Theme }) {
  const [preview, setPreview] = useState<FilePreview | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPreview(null);
    setErr(null);
    if (edge.file_path === '?' || edge.line <= 0) {
      setErr('No file/line for this edge.');
      return;
    }
    getFile({
      repo: edge.repo,
      path: edge.file_path,
      branch: edge.branch ?? 'main',
      start: edge.line,
      end: edge.line,
      context: 8,
    })
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((e) => {
        if (!cancelled) setErr((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [edge]);

  return (
    <div>
      <div className="text-xs text-zinc-500 mb-2 font-mono">
        [{edge.edge_kind}] {edge.caller_qual_name} →{' '}
        {edge.resolved_callee_qual_name ?? edge.callee_name}
        <br />
        {edge.repo}/{edge.file_path}:{edge.line}
      </div>
      {err && <div className="text-xs text-red-500">{err}</div>}
      {preview && (
        <CodeBlock
          code={preview.text}
          language={preview.language ?? null}
          theme={theme}
          startLine={preview.start}
          compact
        />
      )}
    </div>
  );
}
